import {
  Filter,
  Grouping,
  Sorting,
  TableColumnWidthInfo,
} from "@devexpress/dx-react-grid";
import { StateCreator } from "zustand";
import { Store } from "../store";

export type TableIds =
  | "agent"
  | "branch"
  | "branchProduct"
  | "employee"
  | "product"
  | "productBranches"
  | "partner"
  | "tariff"
  | "lead"
  | "calculation"
  | "report"
  | "reportPortfolio"
  | "archive"
  | "leadLog";

export type TableState = {
  columnOrder: string[];
  columnWidths: TableColumnWidthInfo[];
  selection: (string | number)[];
  grouping: Grouping[];
  filters: Filter[];
  sorting: Sorting[];
  hiddenColumnNames: string[];
  search: string;
  expandedGroups: string[];
  expandedRowIds?: (string | number)[];
};

type TablesState = Record<TableIds, TableState>;

export type TableStateChangeFns = {
  onSelectionChange: (selection: (string | number)[]) => void;
  onSortingChange: (sorting: Sorting[]) => void;
  onFiltersChange: (filters: Filter[]) => void;
  onSearchChange: (search: string) => void;
  onGroupingChange: (grouping: Grouping[]) => void;
  onColumnWidthsChange: (columnWidths: TableColumnWidthInfo[]) => void;
  onColumnOrderChange: (columnOrder: string[]) => void;
  onHiddenColumnNamesChange: (hiddenColumnNames: string[]) => void;
  onExpandedGroupsChange: (expandedGroups: string[]) => void;
  onExpandedRowIdsChange: (expandedRowIds: (string | number)[]) => void;
};

export type TablesSlice = {
  tables: TablesState;
  getTableStateChangeFns: (tableId: keyof TablesState) => TableStateChangeFns;
  resetFilters: (tableId: keyof TablesState) => void;
  hideAllColumns: (tableId: keyof TablesState) => void;
  restoreInitialHiddenColumns: (tableId: keyof TablesState) => void;
};

const initialState: TablesState = {
  agent: {
    columnOrder: [
      "partnerId",
      "name",
      "address",
      "phone",
      "email",
      "contact",
      "branch",
      "partner",
      "partnerType",
      "type",
      "verified",
    ],
    columnWidths: [
      { columnName: "partnerId", width: 150 },
      { columnName: "name", width: 150 },
      { columnName: "address", width: 250 },
      { columnName: "phone", width: 150 },
      { columnName: "email", width: 250 },
      { columnName: "contact", width: 150 },
      { columnName: "branch", width: 300 },
      { columnName: "partner", width: 300 },
      { columnName: "partnerType", width: 150 },
      { columnName: "verified", width: 150 },
      { columnName: "type", width: 150 },
    ],
    filters: [],
    grouping: [],
    hiddenColumnNames: ["partnerId"],
    selection: [],
    sorting: [],
    search: "",
    expandedGroups: [],
  },
  branch: {
    columnOrder: [
      "branchId",
      "name",
      "address",
      "phone",
      "email",
      "active",
      "peximCode",
      "workingHours",
      "enabled",
    ],
    columnWidths: [
      { columnName: "branchId", width: 150 },
      { columnName: "name", width: 300 },
      { columnName: "address", width: 300 },
      { columnName: "phone", width: 150 },
      { columnName: "email", width: 300 },
      { columnName: "active", width: 150 },
      { columnName: "peximCode", width: 150 },
      { columnName: "workingHours", width: 200 },
      { columnName: "enabled", width: 150 },
    ],
    filters: [],
    grouping: [],
    hiddenColumnNames: ["branchId"],
    selection: [],
    sorting: [],
    search: "",
    expandedGroups: [],
  },
  employee: {
    columnOrder: [
      "employeeId",
      "firstName",
      "lastName",
      "active",
      "email",
      "function",
      "superior",
      "superiorFunction",
      "branches",
    ],
    columnWidths: [
      { columnName: "employeeId", width: 150 },
      { columnName: "firstName", width: 300 },
      { columnName: "lastName", width: 300 },
      { columnName: "active", width: 100 },
      { columnName: "email", width: 300 },
      { columnName: "function", width: 150 },
      { columnName: "superior", width: 250 },
      { columnName: "superiorFunction", width: 180 },
      { columnName: "branches", width: 600 },
    ],
    filters: [],
    grouping: [],
    hiddenColumnNames: [],
    selection: [],
    sorting: [],
    search: "",
    expandedGroups: [],
  },
  product: {
    columnOrder: ["productId", "name", "description", "expiryDays", "active"],
    columnWidths: [
      { columnName: "productId", width: 150 },
      { columnName: "name", width: 150 },
      { columnName: "description", width: 600 },
      { columnName: "expiryDays", width: 200 },
      { columnName: "active", width: 150 },
    ],
    filters: [],
    grouping: [],
    hiddenColumnNames: ["productId"],
    selection: [],
    sorting: [],
    search: "",
    expandedGroups: [],
  },
  branchProduct: {
    columnOrder: ["productName", "isActive"],
    columnWidths: [
      { columnName: "productId", width: 150 },
      { columnName: "productName", width: 200 },
      { columnName: "isActive", width: 150 },
    ],
    filters: [],
    grouping: [],
    hiddenColumnNames: ["productId"],
    selection: [],
    sorting: [],
    search: "",
    expandedGroups: [],
  },
  productBranches: {
    columnOrder: ["branchName", "isActive"],
    columnWidths: [
      { columnName: "productId", width: 150 },
      { columnName: "branchName", width: 450 },
      { columnName: "isActive", width: 150 },
    ],
    filters: [],
    grouping: [],
    hiddenColumnNames: ["productId"],
    selection: [],
    sorting: [],
    search: "",
    expandedGroups: [],
  },
  partner: {
    columnOrder: [
      "partnerId",
      "verified",
      "partnerType",
      "name",
      "address",
      "phone",
      "email",
      "contact",
      "type",
      "representative",
      "identificationNumber",
      "taxId",
      "accountNumber",
      "billingPeriod",
      "activeTariff",
      "saleChannelId",
      "sme",
      "individual",
      "insertedBy",
      "verifiedBy",
    ],
    columnWidths: [
      { columnName: "partnerId", width: 150 },
      { columnName: "type", width: 150 },
      { columnName: "name", width: 300 },
      { columnName: "address", width: 300 },
      { columnName: "phone", width: 150 },
      { columnName: "email", width: 300 },
      { columnName: "contact", width: 150 },
      { columnName: "representative", width: 150 },
      { columnName: "partnerType", width: 150 },
      { columnName: "identificationNumber", width: 150 },
      { columnName: "taxId", width: 150 },
      { columnName: "accountNumber", width: 150 },
      { columnName: "billingPeriod", width: 150 },
      { columnName: "activeTariff", width: 150 },
      { columnName: "saleChannelId", width: 200 },
      { columnName: "insertedBy", width: 150 },
      { columnName: "verifiedBy", width: 150 },
      { columnName: "verified", width: 150 },
      { columnName: "sme", width: 150 },
      { columnName: "individual", width: 150 },
    ],
    filters: [],
    grouping: [],
    hiddenColumnNames: [],
    selection: [],
    sorting: [],
    search: "",
    expandedGroups: [],
  },
  tariff: {
    columnOrder: [],
    columnWidths: [],
    filters: [],
    grouping: [],
    hiddenColumnNames: [],
    selection: [],
    sorting: [],
    search: "",
    expandedGroups: [],
  },
  lead: {
    columnOrder: [
      "firstName",
      "lastName",
      "leadId",
      "birthDate",
      "residence",
      "phone",
      "email",
      "description",
      "isContacted",
      "employer",
      "amount",
      "currency",
      "acquisitionDate",
      "expired",
      "partner",
      "partnerType",
      "agent",
      "branch",
      "product",
      "daysUntilExpiration",
      "agentCode",
      "expiryDateExtended",
      "createdAt",
      "dateModified",
      "opportunityIdStatus",
      "opportunityIdSubStatus",
      "opportunityIdComment1",
      "opportunityResponse",
      "opportunityIdComment2",
      "meetingStatus",
      "meetingSubStatus",
      "meetingDescription",
      "lastContact",
      "meetingDate",
      "salaryBefore",
      "requestStatus",
      "requestAmount",
      "netoRealizedAmount",
      "lastRequestDate",
      "partnerStatus",
      "decisionNumber",
      "saleChannelId",
      "status",
      "party",
      "pubId",
      "requestNumber",
      "exchangeRate",
      "realizedAt",
      "realizedAmount",
      "calculationNumber",
      "partnerCommission",
      "tariff",
      "included",
      "twoDaysReminder",
      "fourDaysReminder",
      "sevenDaysReminder",
      "exclusionReason",
      "contacted",
      "comment",
      "hasChat",
    ],
    columnWidths: [
      { columnName: "leadId", width: 150 },
      { columnName: "firstName", width: 150 },
      { columnName: "lastName", width: 150 },
      { columnName: "birthDate", width: 150 },
      { columnName: "residence", width: 150 },
      { columnName: "phone", width: 150 },
      { columnName: "email", width: 200 },
      { columnName: "description", width: 600 },
      { columnName: "isContacted", width: 200 },
      { columnName: "meetingDate", width: 150 },
      { columnName: "realizedAt", width: 150 },
      { columnName: "amount", width: 150 },
      { columnName: "realizedAmount", width: 150 },
      { columnName: "currency", width: 150 },
      { columnName: "partnerCommission", width: 150 },
      { columnName: "employer", width: 150 },
      { columnName: "acquisitionDate", width: 150 },
      { columnName: "createdAt", width: 200 },
      { columnName: "included", width: 200 },
      { columnName: "party", width: 150 },
      { columnName: "pubId", width: 100 },
      { columnName: "requestNumber", width: 150 },
      { columnName: "contacted", width: 250 },
      { columnName: "exchangeRate", width: 150 },
      { columnName: "lastContact", width: 150 },
      { columnName: "expired", width: 150 },
      { columnName: "dateModified", width: 250 },
      { columnName: "opportunityIdComment1", width: 600 },
      { columnName: "opportunityIdStatus", width: 200 },
      { columnName: "opportunityResponse", width: 300 },
      { columnName: "opportunityIdComment2", width: 600 },
      { columnName: "opportunityIdSubStatus", width: 200 },
      { columnName: "meetingStatus", width: 150 },
      { columnName: "meetingSubStatus", width: 200 },
      { columnName: "meetingDescription", width: 600 },
      { columnName: "salaryBefore", width: 150 },
      { columnName: "requestStatus", width: 200 },
      { columnName: "requestAmount", width: 150 },
      { columnName: "netoRealizedAmount", width: 200 },
      { columnName: "partner", width: 300 },
      { columnName: "partnerType", width: 150 },
      { columnName: "agent", width: 150 },
      { columnName: "branch", width: 300 },
      { columnName: "product", width: 150 },
      { columnName: "tariff", width: 150 },
      { columnName: "calculationNumber", width: 150 },
      { columnName: "partnerStatus", width: 150 },
      { columnName: "decisionNumber", width: 150 },
      { columnName: "status", width: 150 },
      { columnName: "lastRequestDate", width: 200 },
      { columnName: "daysUntilExpiration", width: 200 },
      { columnName: "agentCode", width: 150 },
      { columnName: "saleChannelId", width: 200 },
      { columnName: "twoDaysReminder", width: 200 },
      { columnName: "fourDaysReminder", width: 200 },
      { columnName: "sevenDaysReminder", width: 200 },
      { columnName: "expiryDateExtended", width: 200 },
      { columnName: "exclusionReason", width: 150 },
      { columnName: "comment", width: 600 },
      { columnName: "hasChat", width: 150 },
    ],
    filters: [],
    grouping: [],
    hiddenColumnNames: [],
    selection: [],
    sorting: [
      { columnName: "twoDaysReminder", direction: "desc" },
      { columnName: "fourDaysReminder", direction: "desc" },
      { columnName: "sevenDaysReminder", direction: "desc" },
      { columnName: "leadId", direction: "desc" },
    ],
    search: "",
    expandedGroups: [],
  },
  calculation: {
    columnOrder: [
      "calculationId",
      "number",
      "partner",
      "partnerType",
      "insertedAt",
      "invoiceId",
      "invoiceSentAt",
      "invoicePaidAt",
      "invoiced",
      "deduction",
      "paid",
      "insertedBy",
      "verifiedBy",
      "uploadInvoice",
      "employeeVerified",
      "verified",
    ],
    columnWidths: [
      { columnName: "calculationId", width: 150 },
      { columnName: "number", width: 150 },
      { columnName: "insertedAt", width: 150 },
      { columnName: "invoiceId", width: 150 },
      { columnName: "invoiceSentAt", width: 200 },
      { columnName: "invoicePaidAt", width: 200 },
      { columnName: "invoiced", width: 200 },
      { columnName: "deduction", width: 200 },
      { columnName: "paid", width: 200 },
      { columnName: "insertedBy", width: 150 },
      { columnName: "verifiedBy", width: 200 },
      { columnName: "partner", width: 300 },
      { columnName: "partnerType", width: 150 },
      { columnName: "verified", width: 250 },
      { columnName: "employeeVerified", width: 250 },
    ],
    filters: [],
    grouping: [],
    hiddenColumnNames: ["calculationId"],
    selection: [],
    sorting: [],
    search: "",
    expandedGroups: [],
    expandedRowIds: [],
  },
  report: {
    columnOrder: [
      "leadId",
      "firstName",
      "lastName",
      "birthDate",
      "residence",
      "phone",
      "email",
      "description",
      "employer",
      "amount",
      "currency",
      "acquisitionDate",
      "comment",
      "expired",
      "partnerName",
      "partnerType",
      "agent",
      "branchName",
      "productName",
      "agentCode",
      "expiryDateExtended",
      "createdAt",
      "dateModified",
      "opportunityIdSubStatus",
      "opportunityIdStatus",
      "opportunityIdComment1",
      "opportunityIdComment2",
      "meetingSubStatus",
      "meetingDescription",
      "lastContact",
      "meetingDate",
      "salaryBefore",
      "requestStatus",
      "requestAmount",
      "netoRealizedAmount",
      "lastRequestDate",
      "partnerStatus",
      "decisionNumber",
      "saleChannelId",
      "party",
      "pubId",
      "requestNumber",
      "exchangeRate",
      "realizedAmount",
      "realizedAt",
      "calculationNumber",
      "partnerCommission",
      "tariffName",
      "excluded",
      "exclusionReason",
      "createExcluded",
      "createExclusionReason",
      "currentExcluded",
      "currentExcludedReason",
      "employerId", //report
      "employerName",
      "mainBranch",
      "productNameCore",
      "category",
      "employerSector",
      "maturityDate",
      "endDate",
      "customerSegment",
      "interest",
      "loanTerm",
      "fixedInterest",
      "interestType",
      "benefitPrice",
      "region",
      "riskRating",
      "riskScoring",
      "deviation",
      "statusParty",
      "amountEarlyPayment",
      "loanApprovedEmployeeId",
      "salary",
      "dtoRatio",
      "contacted",
      "meetingStatus",
      "npl",
      "fraud",
      "died",
      "ptF6",
      "ptF12",
      "etb",
      "accountOpenedAt",
    ],
    columnWidths: [
      { columnName: "leadId", width: 150 },
      { columnName: "firstName", width: 200 },
      { columnName: "lastName", width: 200 },
      { columnName: "birthDate", width: 150 },
      { columnName: "residence", width: 150 },
      { columnName: "phone", width: 150 },
      { columnName: "email", width: 200 },
      { columnName: "description", width: 600 },
      { columnName: "meetingDate", width: 150 },
      { columnName: "amount", width: 150 },
      { columnName: "realizedAmount", width: 150 },
      { columnName: "realizedAt", width: 200 },
      { columnName: "currency", width: 150 },
      { columnName: "partnerCommission", width: 150 },
      { columnName: "employer", width: 150 },
      { columnName: "acquisitionDate", width: 150 },
      { columnName: "createdAt", width: 200 },
      { columnName: "comment", width: 600 },
      { columnName: "excluded", width: 200 },
      { columnName: "createExcluded", width: 200 },
      { columnName: "createExclusionReason", width: 200 },
      { columnName: "currentExcluded", width: 200 },
      { columnName: "currentExcludedReason", width: 200 },
      { columnName: "party", width: 150 },
      { columnName: "saleChannelId", width: 150 },
      { columnName: "pubId", width: 100 },
      { columnName: "requestNumber", width: 150 },
      { columnName: "exchangeRate", width: 150 },
      { columnName: "lastContact", width: 150 },
      { columnName: "expired", width: 150 },
      { columnName: "dateModified", width: 250 },
      { columnName: "opportunityIdComment1", width: 600 },
      { columnName: "opportunityIdComment2", width: 600 },
      { columnName: "opportunityIdSubStatus", width: 200 },
      { columnName: "opportunityIdStatus", width: 200 },
      { columnName: "meetingSubStatus", width: 200 },
      { columnName: "meetingDescription", width: 600 },
      { columnName: "salaryBefore", width: 150 },
      { columnName: "requestStatus", width: 200 },
      { columnName: "requestAmount", width: 150 },
      { columnName: "netoRealizedAmount", width: 200 },
      { columnName: "partnerName", width: 300 },
      { columnName: "partnerType", width: 150 },
      { columnName: "agent", width: 150 },
      { columnName: "branchName", width: 300 },
      { columnName: "productName", width: 150 },
      { columnName: "tariffName", width: 150 },
      { columnName: "calculationNumber", width: 150 },
      { columnName: "partnerStatus", width: 150 },
      { columnName: "decisionNumber", width: 150 },
      { columnName: "lastRequestDate", width: 200 },
      { columnName: "agentCode", width: 150 },
      { columnName: "expiryDateExtended", width: 200 },
      { columnName: "exclusionReason", width: 150 },
      //report
      { columnName: "fixedInterest", width: 150 },
      { columnName: "interest", width: 100 },
      { columnName: "benefitPrice", width: 150 },
      { columnName: "interestType", width: 150 },
      { columnName: "loanTerm", width: 150 },
      { columnName: "loanApprovedEmployeeId", width: 150 },
      { columnName: "category", width: 300 },
      { columnName: "productNameCore", width: 350 },
      { columnName: "employerSector", width: 300 },
      { columnName: "maturityDate", width: 150 },
      { columnName: "customerSegment", width: 200 },
      { columnName: "region", width: 300 },
      { columnName: "riskRating", width: 150 },
      { columnName: "riskScoring", width: 150 },
      { columnName: "deviation", width: 150 },
      { columnName: "employerId", width: 150 },
      { columnName: "employerName", width: 250 },
      { columnName: "mainBranch", width: 250 },
      { columnName: "statusParty", width: 150 },
      { columnName: "endDate", width: 150 },
      { columnName: "amountEarlyPayment", width: 150 },
      { columnName: "salary", width: 150 },
      { columnName: "dtoRatio", width: 150 },
      { columnName: "contacted", width: 150 },
      { columnName: "meetingStatus", width: 150 },
      { columnName: "npl", width: 150 },
      { columnName: "fraud", width: 150 },
      { columnName: "died", width: 150 },
      { columnName: "ptF6", width: 150 },
      { columnName: "ptF12", width: 150 },
      { columnName: "etb", width: 150 },
      { columnName: "accountOpenedAt", width: 150 },
    ],
    filters: [],
    grouping: [],
    hiddenColumnNames: [],
    selection: [],
    sorting: [{ columnName: "leadId", direction: "desc" }],
    search: "",
    expandedGroups: [],
  },
  reportPortfolio: {
    columnOrder: [
      "idRow",
      "partnerName",
      "totalLead",
      "totalCashLoan",
      "totalHouseLoan",
      "totalPayroll",
      "clhlRequest",
      "clhlRequestPercentage",
      "cashLoanRequest",
      "cashLoanRequestPercentage",
      "houseLoanRequest",
      "houseLoanRequestPercentage",
      "clhlRealized",
      "clhlRealizedPercentage",
      "cashLoanRealized",
      "cashLoanRealizedPercentage",
      "houseLoanRealized",
      "houseLoanRealizedPercentage",
      "payrollRealized",
      "clhlPaid",
      "clhlPaidPercentage",
      "cashLoanPaid",
      "cashLoanPaidPercentage",
      "houseLoanPaid",
      "houseLoanPaidPercentage",
      "payrollPaid",
      "totalRejection",
      "totalRejectionRate",
      "totalRejectionDTI",
      "totalRejectionMisc",
      "brutoRealized",
      "brutoCashRealized",
      "brutoHouseRealized",
      "netoRealized",
      "netoPercentage",
      "brutoRealizedPaid",
      "brutoRealizedPaidPercentage",
      "brutoCashRealizedPaid",
      "brutoCashRealizedPaidPercentage",
      "brutoHouseRealizedPaid",
      "brutoHouseRealizedPaidPercentage",
      "partnerCommisionCash",
      "partnerCommisionHouse",
      "partnerCommisionPayroll",
      "partnerCommision",
      "partnerCommisionSaleChannel",
      "partnerCommisionSaleChannelPercentage",
      "avgRequestAmountCash",
      "avgLoanTermCash",
      "avgInterestCash",
      "avgRequestAmountHouse",
      "avgLoanTermHouse",
      "avgInterestHouse",
      "avgIncomeCash",
      "avgIncomeHouse",
      "avgDTI",
      "risk03",
      "risk46",
      "risk67",
      "risk79",
      "salaryBefore",
      "salaryAfter",
      "first6M",
      "first6MAmount",
      "first12M",
      "first12MAmount",

      // novi dodati
      "totalLeadNwPY",
      "totalCashLoanNwPY",
      "totalHouseLoanNwPY",
      "totalPayrollNwPY",
      "totalRejectionWithdrawn",
      "avgWeightedInterestCL",
      "fixedInterestCLPercentage",
      "avgWeightedInterestHL",
      "fixedInterestHLPercentage",
      "avgWeightedFixedInterestCL",
      "first6MPercentage",
      "first12MPercentage",
      "repaymentLastYear",
      "repaymentCurrentYear",
      "first12MAllPortfolio",
      "first12MAllProduction",
      "npl",
      "nplPercentage",
      "nplAmount",
      "nplAmountPeriod",
      "nplAmountProduction",
      "fraud",
      "fraudVolumen",
      "died",
      "outstandingCash",
      "outstandingHouse",
    ],
    columnWidths: [
      { columnName: "idRow", width: 150 },
      { columnName: "partnerName", width: 300 },
      { columnName: "totalLead", width: 200 },
      { columnName: "totalCashLoan", width: 250 },
      { columnName: "totalHouseLoan", width: 250 },
      { columnName: "totalPayroll", width: 200 },
      { columnName: "clhlRequest", width: 220 },
      { columnName: "clhlRequestPercentage", width: 220 },
      { columnName: "cashLoanRequest", width: 200 },
      { columnName: "cashLoanRequestPercentage", width: 350 },
      { columnName: "houseLoanRequest", width: 250 },
      { columnName: "houseLoanRequestPercentage", width: 300 },
      { columnName: "clhlRealized", width: 150 },
      { columnName: "clhlRealizedPercentage", width: 250 },
      { columnName: "cashLoanRealized", width: 200 },
      { columnName: "cashLoanRealizedPercentage", width: 300 },
      { columnName: "houseLoanRealized", width: 200 },
      { columnName: "houseLoanRealizedPercentage", width: 300 },
      { columnName: "payrollRealized", width: 200 },
      { columnName: "clhlPaid", width: 200 },
      { columnName: "clhlPaidPercentage", width: 200 },
      { columnName: "cashLoanPaid", width: 200 },
      { columnName: "cashLoanPaidPercentage", width: 300 },
      { columnName: "houseLoanPaid", width: 200 },
      { columnName: "houseLoanPaidPercentage", width: 300 },
      { columnName: "payrollPaid", width: 200 },

      { columnName: "totalRejection", width: 220 },
      { columnName: "totalRejectionRate", width: 220 },
      { columnName: "totalRejectionDTI", width: 200 },
      { columnName: "totalRejectionMisc", width: 300 },

      { columnName: "brutoRealized", width: 200 },
      { columnName: "brutoCashRealized", width: 250 },
      { columnName: "brutoHouseRealized", width: 200 },
      { columnName: "netoRealized", width: 200 },
      { columnName: "netoPercentage", width: 150 },

      { columnName: "brutoRealizedPaid", width: 150 },
      { columnName: "brutoRealizedPaidPercentage", width: 300 },

      { columnName: "brutoCashRealizedPaid", width: 200 },
      { columnName: "brutoCashRealizedPaidPercentage", width: 250 },
      { columnName: "brutoHouseRealizedPaid", width: 200 },
      { columnName: "brutoHouseRealizedPaidPercentage", width: 200 },

      { columnName: "partnerCommisionCash", width: 220 },
      { columnName: "partnerCommisionHouse", width: 220 },
      { columnName: "partnerCommisionPayroll", width: 200 },
      { columnName: "partnerCommision", width: 200 },
      { columnName: "partnerCommisionSaleChannel", width: 200 },
      { columnName: "partnerCommisionSaleChannelPercentage", width: 250 },

      { columnName: "avgRequestAmountCash", width: 200 },
      { columnName: "avgLoanTermCash", width: 200 },
      { columnName: "avgInterestCash", width: 200 },
      { columnName: "avgRequestAmountHouse", width: 250 },
      { columnName: "avgLoanTermHouse", width: 200 },
      { columnName: "avgInterestHouse", width: 150 },
      { columnName: "avgIncomeCash", width: 200 },
      { columnName: "avgIncomeHouse", width: 200 },

      { columnName: "avgDTI", width: 200 },

      { columnName: "risk03", width: 200 },
      { columnName: "risk46", width: 200 },
      { columnName: "risk67", width: 200 },
      { columnName: "risk79", width: 200 },
      { columnName: "salaryBefore", width: 200 },
      { columnName: "salaryAfter", width: 200 },
      { columnName: "first6M", width: 200 },
      { columnName: "first6MAmount", width: 200 },
      { columnName: "first12M", width: 200 },
      { columnName: "first12MAmount", width: 200 },
      // novi dodati
      { columnName: "totalLeadNwPY", width: 220 },
      { columnName: "totalCashLoanNwPY", width: 220 },
      { columnName: "totalHouseLoanNwPY", width: 200 },
      { columnName: "totalPayrollNwPY", width: 200 },
      { columnName: "totalRejectionWithdrawn", width: 200 },
      { columnName: "avgWeightedInterestCL", width: 200 },
      { columnName: "fixedInterestCLPercentage", width: 200 },
      { columnName: "avgWeightedInterestHL", width: 200 },
      { columnName: "avgWeightedFixedInterestCL", width: 250 },
      { columnName: "first6MPercentage", width: 200 },
      { columnName: "first12MPercentage", width: 150 },
      { columnName: "repaymentLastYear", width: 200 },
      { columnName: "repaymentCurrentYear", width: 200 },

      { columnName: "first12MAllPortfolio", width: 200 },
      { columnName: "first12MAllProduction", width: 200 },
      { columnName: "npl", width: 200 },
      { columnName: "nplPercentage", width: 200 },
      { columnName: "nplAmount", width: 200 },
      { columnName: "nplAmountPeriod", width: 200 },
      { columnName: "nplAmountProduction", width: 200 },
      { columnName: "fraud", width: 200 },
      { columnName: "fraudVolumen", width: 200 },

      { columnName: "died", width: 200 },
      { columnName: "outstandingCash", width: 200 },
      { columnName: "outstandingHouse", width: 200 },
    ],
    filters: [],
    grouping: [],
    hiddenColumnNames: ["idRow"],
    selection: [],
    sorting: [{ columnName: "idRow", direction: "desc" }],
    search: "",
    expandedGroups: [],
  },
  archive: {
    columnOrder: [
      "leadId",
      "firstName",
      "lastName",
      "birthDate",
      "residence",
      "phone",
      "email",
      "description",
      "employer",
      "amount",
      "currency",
      "acquisitionDate",
      "comment",
      "expired",
      "partner",
      "partnerType",
      "agent",
      "branch",
      "product",
      "agentCode",
      "expiryDateExtended",
      "createdAt",
      "dateModified",
      "opportunityIdSubStatus",
      "opportunityIdComment1",
      "opportunityIdComment2",
      "meetingSubStatus",
      "meetingDescription",
      "lastContact",
      "meetingDate",
      "salaryBefore",
      "requestStatus",
      "requestAmount",
      "netoRealizedAmount",
      "lastRequestDate",
      "partnerStatus",
      "decisionNumber",
      "saleChannelId",
      "party",
      "pubId",
      "requestNumber",
      "exchangeRate",
      "realizedAmount",
      "calculationNumber",
      "partnerCommission",
      "tariff",
      "excluded",
      "exclusionReason",
      "employerId", //report
      "employerName",
      "mainBranch",
      "productNameCore",
      "category",
      "employerSector",
      "maturityDate",
      "endDate",
      "customerSegment",
      "interest",
      "loanTerm",
      "fixedInterest",
      "interestType",
      "benefitPrice",
      "region",
      "riskRating",
      "riskScoring",
      "deviation",
      "statusParty",
      "amountEarlyPayment",
      "loanApprovedEmployeeId",
    ],
    columnWidths: [
      { columnName: "leadId", width: 150 },
      { columnName: "firstName", width: 200 },
      { columnName: "lastName", width: 200 },
      { columnName: "birthDate", width: 150 },
      { columnName: "residence", width: 150 },
      { columnName: "phone", width: 150 },
      { columnName: "email", width: 200 },
      { columnName: "description", width: 600 },
      { columnName: "meetingDate", width: 150 },
      { columnName: "amount", width: 150 },
      { columnName: "realizedAmount", width: 150 },
      { columnName: "currency", width: 150 },
      { columnName: "partnerCommission", width: 150 },
      { columnName: "employer", width: 150 },
      { columnName: "acquisitionDate", width: 150 },
      { columnName: "createdAt", width: 200 },
      { columnName: "comment", width: 600 },
      { columnName: "excluded", width: 200 },
      { columnName: "party", width: 150 },
      { columnName: "saleChannelId", width: 150 },
      { columnName: "pubId", width: 100 },
      { columnName: "requestNumber", width: 150 },
      { columnName: "exchangeRate", width: 150 },
      { columnName: "lastContact", width: 150 },
      { columnName: "expired", width: 150 },
      { columnName: "dateModified", width: 250 },
      { columnName: "opportunityIdComment1", width: 600 },
      { columnName: "opportunityIdComment2", width: 600 },
      { columnName: "opportunityIdSubStatus", width: 200 },
      { columnName: "meetingSubStatus", width: 200 },
      { columnName: "meetingDescription", width: 600 },
      { columnName: "salaryBefore", width: 150 },
      { columnName: "requestStatus", width: 200 },
      { columnName: "requestAmount", width: 150 },
      { columnName: "netoRealizedAmount", width: 200 },
      { columnName: "partner", width: 300 },
      { columnName: "partnerType", width: 150 },
      { columnName: "agent", width: 150 },
      { columnName: "branch", width: 300 },
      { columnName: "product", width: 150 },
      { columnName: "tariff", width: 150 },
      { columnName: "calculationNumber", width: 150 },
      { columnName: "partnerStatus", width: 150 },
      { columnName: "decisionNumber", width: 150 },
      { columnName: "lastRequestDate", width: 200 },
      { columnName: "agentCode", width: 150 },
      { columnName: "expiryDateExtended", width: 200 },
      { columnName: "exclusionReason", width: 150 },
      //report
      { columnName: "fixedInterest", width: 150 },
      { columnName: "interest", width: 100 },
      { columnName: "benefitPrice", width: 150 },
      { columnName: "interestType", width: 150 },
      { columnName: "loanTerm", width: 150 },
      { columnName: "loanApprovedEmployeeId", width: 150 },
      { columnName: "category", width: 300 },
      { columnName: "productNameCore", width: 350 },
      { columnName: "employerSector", width: 300 },
      { columnName: "maturityDate", width: 150 },
      { columnName: "customerSegment", width: 200 },
      { columnName: "region", width: 300 },
      { columnName: "riskRating", width: 150 },
      { columnName: "riskScoring", width: 150 },
      { columnName: "deviation", width: 150 },
      { columnName: "employerId", width: 150 },
      { columnName: "employerName", width: 250 },
      { columnName: "mainBranch", width: 250 },
      { columnName: "statusParty", width: 150 },
      { columnName: "endDate", width: 150 },
      { columnName: "amountEarlyPayment", width: 150 },
    ],
    filters: [],
    grouping: [],
    hiddenColumnNames: [],
    selection: [],
    sorting: [{ columnName: "leadId", direction: "desc" }],
    search: "",
    expandedGroups: [],
  },
  leadLog: {
    columnOrder: [
      "leadId",
      "userName",
      "dateAndTime",
      "columnsChanged",
      "firstName",
      "lastName",
      "birthDate",
      "residence",
      "phone",
      "email",
      "description",
      "employer",
      "amount",
      "currency",
      "acquisitionDate",
      "expired",
      "partner",
      "partnerType",
      "agent",
      "branch",
      "product",
      "agentCode",
      "expiryDateExtended",
      "createdAt",
      "dateModified",
      "opportunityIdSubStatus",
      "opportunityIdComment1",
      "opportunityIdComment2",
      "meetingSubStatus",
      "meetingDescription",
      "lastContact",
      "meetingDate",
      "salaryBefore",
      "requestStatus",
      "requestAmount",
      "netoRealizedAmount",
      "lastRequestDate",
      "partnerStatus",
      "decisionNumber",
      "saleChannelId",
      "party",
      "pubId",
      "requestNumber",
      "exchangeRate",
      "realizedAmount",
      "calculationNumber",
      "partnerCommission",
      "tariff",
      "excluded",
      "exclusionReason",
      "employerId", //report
      "employerName",
      "mainBranch",
      "productNameCore",
      "category",
      "employerSector",
      "maturityDate",
      "endDate",
      "customerSegment",
      "interest",
      "loanTerm",
      "fixedInterest",
      "interestType",
      "benefitPrice",
      "region",
      "riskRating",
      "riskScoring",
      "deviation",
      "statusParty",
      "amountEarlyPayment",
      "loanApprovedEmployeeId",
      "comment",
    ],
    columnWidths: [
      { columnName: "leadId", width: 150 },
      { columnName: "userName", width: 200 },
      { columnName: "dateAndTime", width: 200 },
      { columnName: "columnsChanged", width: 200 },
      { columnName: "firstName", width: 200 },
      { columnName: "lastName", width: 200 },
      { columnName: "birthDate", width: 150 },
      { columnName: "residence", width: 150 },
      { columnName: "phone", width: 150 },
      { columnName: "email", width: 200 },
      { columnName: "description", width: 600 },
      { columnName: "meetingDate", width: 150 },
      { columnName: "amount", width: 150 },
      { columnName: "realizedAmount", width: 150 },
      { columnName: "currency", width: 150 },
      { columnName: "partnerCommission", width: 150 },
      { columnName: "employer", width: 150 },
      { columnName: "acquisitionDate", width: 150 },
      { columnName: "createdAt", width: 200 },
      { columnName: "comment", width: 600 },
      { columnName: "excluded", width: 200 },
      { columnName: "party", width: 150 },
      { columnName: "saleChannelId", width: 150 },
      { columnName: "pubId", width: 100 },
      { columnName: "requestNumber", width: 150 },
      { columnName: "exchangeRate", width: 150 },
      { columnName: "lastContact", width: 150 },
      { columnName: "expired", width: 150 },
      { columnName: "dateModified", width: 250 },
      { columnName: "opportunityIdComment1", width: 600 },
      { columnName: "opportunityIdComment2", width: 600 },
      { columnName: "opportunityIdSubStatus", width: 200 },
      { columnName: "meetingSubStatus", width: 200 },
      { columnName: "meetingDescription", width: 600 },
      { columnName: "salaryBefore", width: 150 },
      { columnName: "requestStatus", width: 200 },
      { columnName: "requestAmount", width: 150 },
      { columnName: "netoRealizedAmount", width: 200 },
      { columnName: "partner", width: 300 },
      { columnName: "partnerType", width: 200 },
      { columnName: "agent", width: 150 },
      { columnName: "branch", width: 300 },
      { columnName: "product", width: 150 },
      { columnName: "tariff", width: 150 },
      { columnName: "calculationNumber", width: 150 },
      { columnName: "partnerStatus", width: 150 },
      { columnName: "decisionNumber", width: 150 },
      { columnName: "lastRequestDate", width: 200 },
      { columnName: "agentCode", width: 150 },
      { columnName: "expiryDateExtended", width: 200 },
      { columnName: "exclusionReason", width: 150 },
      //report
      { columnName: "fixedInterest", width: 150 },
      { columnName: "interest", width: 100 },
      { columnName: "benefitPrice", width: 150 },
      { columnName: "interestType", width: 150 },
      { columnName: "loanTerm", width: 150 },
      { columnName: "loanApprovedEmployeeId", width: 150 },
      { columnName: "category", width: 300 },
      { columnName: "productNameCore", width: 350 },
      { columnName: "employerSector", width: 300 },
      { columnName: "maturityDate", width: 150 },
      { columnName: "customerSegment", width: 200 },
      { columnName: "region", width: 300 },
      { columnName: "riskRating", width: 150 },
      { columnName: "riskScoring", width: 150 },
      { columnName: "deviation", width: 150 },
      { columnName: "employerId", width: 150 },
      { columnName: "employerName", width: 250 },
      { columnName: "mainBranch", width: 250 },
      { columnName: "statusParty", width: 150 },
      { columnName: "endDate", width: 150 },
      { columnName: "amountEarlyPayment", width: 150 },
    ],
    filters: [],
    grouping: [],
    hiddenColumnNames: [],
    selection: [],
    sorting: [{ columnName: "dateAndTime", direction: "desc" }],
    search: "",
    expandedGroups: [],
  },
};

export const createTablesSlice: StateCreator<Store, [], [], TablesSlice> = (
  set
) => ({
  tables: initialState,
  getTableStateChangeFns: (tableId) => ({
    onSelectionChange: (selection) =>
      set((state) => ({
        tables: {
          ...state.tables,
          [tableId]: { ...state.tables[tableId], selection },
        },
      })),
    onColumnOrderChange: (columnOrder) =>
      set((state) => ({
        tables: {
          ...state.tables,
          [tableId]: { ...state.tables[tableId], columnOrder },
        },
      })),
    onColumnWidthsChange: (columnWidths) =>
      set((state) => ({
        tables: {
          ...state.tables,
          [tableId]: { ...state.tables[tableId], columnWidths },
        },
      })),
    onExpandedGroupsChange: (expandedGroups) =>
      set((state) => ({
        tables: {
          ...state.tables,
          [tableId]: { ...state.tables[tableId], expandedGroups },
        },
      })),
    onExpandedRowIdsChange: (expandedRowIds) =>
      set((state) => ({
        tables: {
          ...state.tables,
          [tableId]: { ...state.tables[tableId], expandedRowIds },
        },
      })),
    onFiltersChange: (filters) =>
      set((state) => ({
        tables: {
          ...state.tables,
          [tableId]: { ...state.tables[tableId], filters },
        },
      })),
    onGroupingChange: (grouping) =>
      set((state) => ({
        tables: {
          ...state.tables,
          [tableId]: { ...state.tables[tableId], grouping },
        },
      })),
    onHiddenColumnNamesChange: (hiddenColumnNames) =>
      set((state) => ({
        tables: {
          ...state.tables,
          [tableId]: { ...state.tables[tableId], hiddenColumnNames },
        },
      })),
    onSearchChange: (search) =>
      set((state) => ({
        tables: {
          ...state.tables,
          [tableId]: { ...state.tables[tableId], search },
        },
      })),
    onSortingChange: (sorting) =>
      set((state) => ({
        tables: {
          ...state.tables,
          [tableId]: { ...state.tables[tableId], sorting },
        },
      })),
  }),
  resetFilters: (tableId) =>
    set((state) => ({
      tables: {
        ...state.tables,
        [tableId]: {
          ...state.tables[tableId],
          sorting: initialState[tableId].sorting,
          filters: [],
          grouping: [],
          expandedGroups: [],
          search: "",
        },
      },
    })),
  hideAllColumns: (tableId) =>
    set((state) => ({
      tables: {
        ...state.tables,
        [tableId]: {
          ...state.tables[tableId],
          hiddenColumnNames: [...state.tables[tableId].columnOrder],
        },
      },
    })),
  restoreInitialHiddenColumns: (tableId) =>
    set((state) => ({
      tables: {
        ...state.tables,
        [tableId]: {
          ...state.tables[tableId],
          hiddenColumnNames: initialState[tableId].hiddenColumnNames,
        },
      },
    })),
});
